import { createSlice } from '@reduxjs/toolkit';
import LoadingStatus from 'enums/loadingStatus';
import LotStatus from '../enums/lotStatus';
import { deriveActiveChip } from '../services/mapChipService';
import { TAB_FLOOR_PLANS, VIEW_LISTING } from '../constants';

/** Redux slice key used to mount this reducer in the shared store. */
export const DATA_SLICE_KEY = 'nativeInteractiveSiteMapData';

export function createNativeInteractiveSiteMapDataSlice({
  fetchSiteMapStatus = LoadingStatus.Idle.toString(),
  fetchSiteMapError = null,
  fetchLotInventoryStatus = LoadingStatus.Idle.toString(),
  fetchLotInventoryError = null,
} = {}) {
  return createSlice({
    name: DATA_SLICE_KEY,
    initialState: {
      fetchSiteMapStatus,
      fetchSiteMapError,
      fetchLotInventoryStatus,
      fetchLotInventoryError,
      lots: [],
      housePlans: [],
      housingUnits: [],
      soldLots: [],
      searchParameters: {
        lotStatus: LotStatus.Available.toString(),
      },
      viewState: {
        activeView: VIEW_LISTING,
        activeViewContext: {
          planId: null,
          selectedLotId: null,
          housingUnitId: null,
        },
        selectedTab: TAB_FLOOR_PLANS,
        viewHistory: [],
      },
    },
    reducers: {
      fetchSiteMap: (state) => {
        state.fetchSiteMapStatus = LoadingStatus.Loading.toString();
        state.fetchSiteMapError = null;
      },
      fetchSiteMapSuccess: (state, action) => {
        state.fetchSiteMapStatus = LoadingStatus.Succeeded.toString();
        state.lots = action.payload?.lots;
      },
      fetchSiteMapError: (state, action) => {
        state.fetchSiteMapStatus = LoadingStatus.Failed.toString();
        state.fetchSiteMapError = action.payload;
      },
      fetchLotInventory: (state) => {
        state.fetchLotInventoryStatus = LoadingStatus.Loading.toString();
        state.fetchLotInventoryError  = null;
      },
      fetchLotInventorySuccess: (state, action) => {
        state.fetchLotInventoryStatus = LoadingStatus.Succeeded.toString();

        const inventoryLots = action.payload?.lots ?? [];
        const inventoryMap  = Object.fromEntries(
          inventoryLots.map((lot) => [lot.sitePlanLotId, lot])
        );

        // Additive enrichment only site-plan fields (status, housePlanIds, housingUnitId)
        // are left untouched; inventory adds display/geometry data not present in the site-plan
        // call, plus the integer lotId — housePlans (Lots[]) and housingUnits (LotId) reference
        // lots by that integer id, not by the site-plan's sitePlanLotId string.
        // Direct Immer mutation means only matched lots get new object references, keeping
        // unmatched lots stable for distinctUntilChanged selectors.
        state.lots.forEach((lot) => {
          const inventoryLot = inventoryMap[lot.sitePlanLotId];

          if (!inventoryLot) {
            return;
          }

          lot.lotId        = inventoryLot.lotId;
          lot.lotSize      = inventoryLot.lotSize;
          lot.formattedLotSize = inventoryLot.formattedLotSize;
          lot.displayLabel = inventoryLot.displayLabel;
          lot.lotGroupName = inventoryLot.lotGroupName;
          lot.lotType      = inventoryLot.lotType;
          lot.lotFacing    = inventoryLot.lotFacing;
        });

        state.housePlans   = action.payload?.housePlans   ?? [];
        state.housingUnits = action.payload?.housingUnits ?? [];
        state.soldLots     = action.payload?.soldLots     ?? [];
      },
      fetchLotInventoryError: (state, action) => {
        state.fetchLotInventoryStatus = LoadingStatus.Failed.toString();
        state.fetchLotInventoryError  = action.payload;
      },
      lotSelected: (state, action) => {
        state.viewState.activeViewContext.selectedLotId = action.payload ?? null;
      },
      floorPlanCardSelected: (state, action) => {
        state.viewState.activeViewContext.planId = action.payload ?? null;
        state.viewState.activeViewContext.housingUnitId = null;
      },
      housingUnitCardSelected: (state, action) => {
        state.viewState.activeViewContext.planId = null;
        state.viewState.activeViewContext.housingUnitId = action.payload ?? null;
      },
      chipSelected: (state, action) => {
        state.searchParameters.lotStatus = action.payload ?? null;
        // Tabs only exist for the Available chip (NHS20-8054) — clear selectedTab for every
        // other chip, and default it back to Floor Plans when Available becomes active again.
        state.viewState.selectedTab = deriveActiveChip(state.searchParameters) === LotStatus.Available.toString()
          ? TAB_FLOOR_PLANS
          : null;
        state.viewState.activeView = VIEW_LISTING;
        state.viewState.activeViewContext = { planId: null, selectedLotId: null, housingUnitId: null };
        state.viewState.viewHistory = [];
      },
      tabSelected: (state, action) => {
        state.viewState.selectedTab = action.payload;
      },
      // Marker action for mapBackgroundClickedEpic — that epic decides the real transition
      // (navigateBack vs. clearing the selection) based on viewHistory; this reducer is a no-op.
      mapBackgroundClicked: () => {},
      navigateTo: (state, action) => {
        // Only push a history entry when the view actually changes (one entry per distinct
        // activeView; switching lots within the same view just updates it in place). Save the
        // current context so we can restore it when navigating back.
        if (state.viewState.activeView !== action.payload.view) {
          state.viewState.viewHistory.push({
            activeView: state.viewState.activeView,
            activeViewContext: { ...state.viewState.activeViewContext },
            selectedTab: state.viewState.selectedTab,
          });
          state.viewState.activeView = action.payload.view;
        }
        state.viewState.activeViewContext = action.payload.context;
      },
      navigateBack: (state) => {
        const previous = state.viewState.viewHistory.pop();
        if (!previous) {
          return;
        }
        state.viewState.activeView = previous.activeView;
        // Listing's own activeViewContext is always fully null (see chipSelected/viewReset) —
        // but the entry pushed for it in navigateTo can carry a stale, non-null selectedLotId:
        // navigateTo's history push snapshots activeViewContext AFTER lotSelected(lotId) has
        // already mutated it (both dispatched from the same lot click, see lotSelectedEpic.js),
        // so a lot click that routes straight out of listing records the just-clicked lot as if
        // it were already selected on listing itself. Left uncorrected, navigating back (or a
        // background click, which also calls navigateBack once view history exists) restores
        // that stale selection and the lot never visually deselects. Force the same clean-slate
        // context chipSelected/viewReset already use rather than trusting the popped snapshot.
        state.viewState.activeViewContext = previous.activeView === VIEW_LISTING
          ? { planId: null, selectedLotId: null, housingUnitId: null }
          : previous.activeViewContext;
        state.viewState.selectedTab = previous.selectedTab;
      },
      // Dispatched on mobile-modal teardown (see index.js's onStop) — the dataSlice itself is
      // created once per page and outlives any single modal open/close cycle, so without this,
      // reopening the modal after having navigated into a sub-view (e.g. lotMapSelection) would
      // resume there instead of starting fresh at the default listing view.
      viewReset: (state) => {
        state.viewState.activeView = VIEW_LISTING;
        state.viewState.activeViewContext = { planId: null, selectedLotId: null, housingUnitId: null };
        // Same rule as chipSelected — searchParameters.lotStatus survives the reset (the
        // user's filter should persist across a reopen), so selectedTab must be re-derived
        // from it rather than hard-coded, or a non-Available chip would come back paired with
        // a Floor Plans tab that isn't supposed to exist for it.
        state.viewState.selectedTab = deriveActiveChip(state.searchParameters) === LotStatus.Available.toString()
          ? TAB_FLOOR_PLANS
          : null;
        state.viewState.viewHistory = [];
      },
    },
  });
}
